import * as THREE from "three";
import { disposeEnclaveTerrace } from "./baseTheme.js";
import { disposeGroves } from "./nativeGrove.js";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";
import { BloomEffect, EffectComposer, EffectPass, RenderPass, SMAAEffect } from "postprocessing";
import { profileFor, type Quality } from "./quality.js";
import { buildGround, buildSky } from "./ground.js";

export interface SceneHandle {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  composer: EffectComposer;
  controls: OrbitControls;
  quality: Quality;
  setQuality(q: Quality): void;
  onFrame(cb: (dt: number, now: number) => void): () => void;
  resize(width: number, height: number): void;
  /** Sets the day/night blend (0 = noon, 1 = midnight). */
  setNight(n: number): void;
  fps: number;
  frames: number;
  drawCalls: number;
  triangles: number;
  dispose(): void;
}

/** Physically based renderer with ACES, bloom on emissives and SMAA on high (90-art.md R92). */
export function createRenderer(canvas: HTMLCanvasElement, initial: Quality): SceneHandle {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: false,
    powerPreference: "high-performance",
    alpha: false,
    stencil: false,
    depth: true,
  });
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.15;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.shadowMap.type = THREE.PCFShadowMap;
  renderer.info.autoReset = false;

  const scene = new THREE.Scene();
  scene.fog = new THREE.Fog(0xc4d6bd, 55, 150);
  // A neutral studio environment gives the metallic palette materials something to reflect (no image files, R95).
  const pmrem = new THREE.PMREMGenerator(renderer);
  const room = new RoomEnvironment();
  const environmentTarget = pmrem.fromScene(room, 0.04);
  const environment = environmentTarget.texture;
  room.dispose();
  scene.environmentIntensity = 0.45;
  pmrem.dispose();
  const camera = new THREE.PerspectiveCamera(42, 1, 0.5, 500);
  camera.position.set(17, 16, 20);

  const hemi = new THREE.HemisphereLight(0xc9e7ef, 0x9a9360, 1.5);
  scene.add(hemi);
  const key = new THREE.DirectionalLight(0xffeed7, 2.2);
  key.position.set(18, 30, 12);
  key.castShadow = true;
  key.shadow.camera.left = -22;
  key.shadow.camera.right = 22;
  key.shadow.camera.top = 22;
  key.shadow.camera.bottom = -22;
  key.shadow.camera.near = 5;
  key.shadow.camera.far = 90;
  key.shadow.bias = -0.0008;
  scene.add(key);
  const rim = new THREE.DirectionalLight(0xa4d6de, 0.65);
  rim.position.set(-20, 12, -18);
  scene.add(rim);
  // Camera-side fill so building facades never fall to black.
  const fill = new THREE.DirectionalLight(0xb9c8e0, 0.9);
  fill.position.set(-14, 18, 24);
  scene.add(fill);

  const sky = buildSky();
  scene.add(sky);
  const ground = buildGround();
  scene.add(ground);

  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.minPolarAngle = 0.6;
  controls.maxPolarAngle = 1.2;
  controls.minDistance = 18;
  controls.maxDistance = 60;
  controls.enablePan = true;
  controls.screenSpacePanning = false;
  controls.target.set(0, 0, 0);
  controls.update();

  const composer = new EffectComposer(renderer, { multisampling: 0 });
  const renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);
  let effectPass: EffectPass | null = null;

  const handle: SceneHandle = {
    scene,
    camera,
    renderer,
    composer,
    controls,
    quality: initial,
    fps: 0,
    frames: 0,
    drawCalls: 0,
    triangles: 0,
    setQuality(q) {
      const p = profileFor(q);
      handle.quality = q;
      // Metallic buildings still need reflected light on low settings; removing it turns their surfaces black.
      scene.environment = environment;
      renderer.setPixelRatio(p.pixelRatio);
      renderer.shadowMap.enabled = p.shadows;
      key.castShadow = p.shadows;
      key.shadow.mapSize.set(p.shadowMapSize, p.shadowMapSize);
      if (key.shadow.map) {
        key.shadow.map.dispose();
        key.shadow.map = null;
      }
      if (effectPass) {
        composer.removePass(effectPass);
        effectPass.dispose();
        effectPass = null;
      }
      const effects = [];
      if (p.bloom)
        effects.push(
          new BloomEffect({
            luminanceThreshold: 1.1,
            luminanceSmoothing: 0.2,
            intensity: 0.4,
            mipmapBlur: true,
          }),
        );
      if (p.smaa) effects.push(new SMAAEffect());
      if (effects.length) {
        effectPass = new EffectPass(camera, ...effects);
        composer.addPass(effectPass);
      }
    },
    onFrame(cb) {
      frameCallbacks.add(cb);
      return () => frameCallbacks.delete(cb);
    },
    resize(width, height) {
      camera.aspect = width / Math.max(1, height);
      camera.updateProjectionMatrix();
      renderer.setSize(width, height, false);
      composer.setSize(width, height);
    },
    setNight(n) {
      const night = THREE.MathUtils.clamp(n, 0, 1);
      key.intensity = THREE.MathUtils.lerp(2.2, 1.3, night);
      key.color.set(new THREE.Color(0xffeed7).lerp(new THREE.Color(0x9fbcff), night));
      hemi.intensity = THREE.MathUtils.lerp(1.5, 0.85, night);
      fill.intensity = THREE.MathUtils.lerp(0.9, 0.55, night);
      (sky.material as THREE.ShaderMaterial).uniforms["night"]!.value = night;
      scene.environmentIntensity = THREE.MathUtils.lerp(0.45, 0.25, night);
      (scene.fog as THREE.Fog).color.set(0xc4d6bd).lerp(new THREE.Color(0x213b50), night);
      renderer.toneMappingExposure = THREE.MathUtils.lerp(1.0, 0.95, night);
    },
    dispose() {
      running = false;
      cancelAnimationFrame(raf);
      controls.dispose();
      composer.dispose();
      // Feature scenes own their instances. Retire only this renderer's native ground, sky and lighting.
      const terrace = ground.getObjectByName("enclave.terrace");
      if (terrace) disposeEnclaveTerrace(terrace);
      disposeGroves(ground);
      const geometries = new Set<THREE.BufferGeometry>();
      const materials = new Set<THREE.Material>();
      const textures = new Set<THREE.Texture>();
      for (const root of [ground, sky]) root.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return;
        geometries.add(object.geometry);
        for (const material of Array.isArray(object.material) ? object.material : [object.material]) {
          materials.add(material);
          if (material instanceof THREE.MeshStandardMaterial && material.map) textures.add(material.map);
        }
      });
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
      for (const texture of textures) texture.dispose();
      key.shadow.dispose();
      environmentTarget.dispose();
      renderer.dispose();
      queueMicrotask(() => {
        if (!canvas.isConnected) renderer.forceContextLoss();
      });
    },
  };

  const frameCallbacks = new Set<(dt: number, now: number) => void>();
  let running = true;
  let raf = 0;
  let last = performance.now();
  let fpsAccum = 0;
  let fpsFrames = 0;
  const loop = (now: number) => {
    if (!running) return;
    raf = requestAnimationFrame(loop);
    const elapsed = (now - last) / 1000;
    const dt = Math.min(0.1, elapsed);
    last = now;
    controls.update();
    for (const cb of frameCallbacks) cb(dt, now);
    renderer.info.reset();
    composer.render(dt);
    handle.drawCalls = renderer.info.render.calls;
    handle.triangles = renderer.info.render.triangles;
    handle.frames++;
    fpsAccum += elapsed;
    fpsFrames++;
    if (fpsAccum >= 0.5) {
      handle.fps = Math.round(fpsFrames / fpsAccum);
      fpsAccum = 0;
      fpsFrames = 0;
    }
  };
  handle.setQuality(initial);
  raf = requestAnimationFrame(loop);
  return handle;
}
